import type { ModuleSnapshot } from '@ctcells/contract'

import type { ChamberDetail } from '../../figures/chamber3d/chamberScene.ts'

import { holdKindOf } from './monitoringModel.ts'

/**
 * **챔버 3D 의 세부 국면 — 서버 모듈 상태에서** (eric 2026-09-28 「어셈블러는 팁으로 뒤에서 누르는 거랑.. uv 상태에서는
 * uv를 밑에서 쏘는 3d가 필요」).
 *
 * 🔴 **서버가 말하는 것만 쓴다.** 챔버 한 개의 공정 자리(단계 · 진행/완료)는 배치 표지가 정하고(`chamberPhase`),
 *    그 단계 «안»의 국면은 **그 배치의 실행을 쥐고 있다고 서버가 말한 모듈**의 `state` 가 정한다.
 *    실물 전수(2026-09-28 · 작업 `10b71573` `GET /history` 5,644건의 `module_state_change`) —
 *
 * | 모듈 | 공정 중 상태 전이(실측) | 3D 에 쓰는 것 |
 * | --- | --- | --- |
 * | 원심분리기 | `LOADING → RUNNING → UNLOADING` (회차 3번) | `RUNNING` 이고 정지(`hold` · `e_stop`) 아님 → `spin` · 그 밖 `rest` |
 * | 어셈블러 | `LOADING → TC_LOAD → UV → TC_REMOVE → ROTATE_DRAIN → UNLOADING` (챔버 9번 · 사이사이 `MOVING`) | **`UV` 면 `uv`** · 그 밖은 기본(누름) |
 * | 염색기 | `TIP_CHANGE · PIPETTING · DISPENSING · MOVING` 반복 → `SHAKING → INCUBATING → WAITING` | 흔들기 · 배양 · 대기면 `rest`(스포이드 없음) |
 *
 * ⚠ **어느 웰을 하는지는 모듈 상태에 없다**(`module_status` 에 웰 칸 없음 · `chamberProgress.ts` 머리말). 그래서 이
 *   국면은 **그 단계에 있는 챔버 전부**에 같이 입는다. 어셈블러가 챔버를 하나씩 UV 에 넣어도 화면은 누가 지금
 *   UV 인지 모른다 — 짐작해 한 칸만 비추지 않는다.
 * ⚠ 어셈블러의 `TC_LOAD`·`MOVING` 을 따로 가르지 않는다 — 1초 안팎으로 바뀌어 도구가 깜빡이기만 한다.
 */

/** 모듈 종류 → 그 모듈이 하는 공정 단계(계약 `SampleStage` 어휘) */
const STAGE_OF_TYPE: Readonly<Record<string, 'centrifuged' | 'assembled' | 'staining'>> = {
  centrifuge: 'centrifuged',
  assembler: 'assembled',
  staining: 'staining',
}

/** 염색기가 분주를 쉬는 국면 — 흔들기 · 배양 · 대기 · 끝 */
const STAIN_REST: ReadonlySet<string> = new Set(['SHAKING', 'INCUBATING', 'WAITING', 'COMPLETE'])

/** 모듈 종류 · 상태 → 세부 국면. 🔴 순수 함수. 모르면 `undefined`(그 단계의 기본 도구) */
export function chamberStepOf(
  moduleType: string | undefined,
  state: string | undefined,
): ChamberDetail['step'] {
  if (moduleType === undefined || state === undefined) return undefined
  const stage = STAGE_OF_TYPE[moduleType]
  if (stage === undefined) return undefined
  switch (stage) {
    /*
     * 🔴 원심은 **「돈다」를 서버가 말할 때만** 돈다(2026-10-02 · 시연·쵸단 검토). 종전에는 `RUNNING` 이면 `undefined` 를
     *    내 그림의 기본 도구(회전)에 맡겼는데, 그 기본은 «모를 때»에도 서서 끊김 · 낡은 스냅샷 · 실행 모듈 못 찾음에서
     *    로터가 도는 것처럼 그렸다. 이제 `RUNNING` 은 `spin`, 그 밖(적재 · 회수 · 이동 · 대기)은 `rest` 이고,
     *    모르면(`undefined`) 그림이 도구를 안 얹는다(`chamberTool`).
     */
    case 'centrifuged':
      return { stage, step: state === 'RUNNING' ? 'spin' : 'rest' }
    case 'assembled':
      return state === 'UV' ? { stage, step: 'uv' } : undefined
    default:
      return STAIN_REST.has(state) ? { stage, step: 'rest' } : undefined
  }
}

/**
 * **그 배치의 실행을 쥔 모듈**의 세부 국면. 🔴 `current_execution.batch_id` 가 그 배치인 모듈 하나만 본다 —
 * 같은 종류 모듈이 여럿이어도(실물: `assembler-1` · `mock-assembler-1`) 이름으로 고르지 않는다.
 * 🔴 **끊겼거나(`stale`) 모듈 스냅샷이 낡았으면 모른다** — 마지막 UV 를 「지금」으로 그리지 않는다.
 */
export function runningChamberStep(input: {
  modules: readonly ModuleSnapshot[] | undefined
  batchId: string | undefined
  stale: boolean
}): ChamberDetail['step'] {
  const { modules, batchId, stale } = input
  if (stale || batchId === undefined || modules === undefined) return undefined
  const running = modules.find((module) => module.current_execution?.batch_id === batchId)
  if (running === undefined || running.stale) return undefined
  const step = chamberStepOf(running.module_type, running.state)
  /*
   * 🔴 **멈춘 로터는 안 돈다.** 일시정지 · 안전정지는 `state` 를 그대로 두고 `hold` 만 세운다
   *    (계약 `ModuleStatus.hold` 「state 는 물리 공정을 유지하고 정지·일시정지는 이 필드가 표현한다」 ·
   *    module-core `app/api/safety.py` `pause_module`). 그래서 `RUNNING` + `hold.kind: user_pause` 가 실제로 온다.
   *    정지(`holdKindOf`)거나 HW 비상정지(`e_stop: true`)면 회전 호를 걷는다. ⚠ 원심에만 건다 — 어셈 UV · 염색 분주의
   *    같은 문제는 이번 범위 밖이다.
   */
  if (step?.step === 'spin' && (holdKindOf(running) !== undefined || running.e_stop === true)) {
    return { stage: step.stage, step: 'rest' }
  }
  return step
}
